import type {DocumentNode} from "graphql";
import gql from "graphql-tag";

import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {OfficialClarification} from "./types.ts";

const officialClarificationFragment: DocumentNode = gql`
    fragment clarificationFragment on OfficialClarification {
        id
        text
        openTimestamp
        closeTimestamp
        youtubeId
    }
`;

const officialClarificationWithReporterFragment: DocumentNode = gql`
    ${officialClarificationFragment}

    fragment clarificationFragmentWithReporter on OfficialClarification {
        reporter {
            id
            kaid
            nickname
            avatarSrc: avatarUrl
        }
        ...clarificationFragment
    }
`;

export type OfficialClarificationsData = {
    officialClarifications: Array<OfficialClarification>;
};

type OfficialClarificationsVariables = {
    youtubeId: string;
};

export const GET_OFFICIAL_CLARIFICATIONS_WITH_REPORTER_QUERY: GqlOperation<
    OfficialClarificationsData,
    OfficialClarificationsVariables
> = gqlOp(gql`
    query getOfficialClarificationsWithReporter($youtubeId: String!) {
        officialClarifications(youtubeId: $youtubeId) {
            ...clarificationFragmentWithReporter
        }
    }

    ${officialClarificationWithReporterFragment}
`);

export const GET_OFFICIAL_CLARIFICATIONS_WITHOUT_REPORTER_QUERY: GqlOperation<
    OfficialClarificationsData,
    OfficialClarificationsVariables
> = gqlOp(gql`
    query getOfficialClarifications($youtubeId: String!) {
        officialClarifications(youtubeId: $youtubeId) {
            ...clarificationFragment
        }
    }

    ${officialClarificationFragment}
`);

type CreateClarificationMutationData = {
    createOfficialClarification: {
        newClarification: OfficialClarification | null | undefined;
        error: {
            code: string;
        };
    };
};

type CreateClarificationVariables = {
    clarification: {
        youtubeId: string;
        reporterKaid: string;
        text: string;
        openTimestamp: number;
        closeTimestamp: number;
    };
};

const CREATE_OFFICIAL_CLARIFICATION_MUTATION: GqlOperation<
    CreateClarificationMutationData,
    CreateClarificationVariables
> = gqlOp(gql`
    mutation createOfficialClarification(
        $clarification: CreateOfficialClarificationInput!
    ) {
        createOfficialClarification(clarification: $clarification) {
            newClarification {
                ...clarificationFragmentWithReporter
            }
            error {
                code
            }
        }
    }

    ${officialClarificationWithReporterFragment}
`);
export const CREATE_OFFICIAL_CLARIFICATION_MUTATION_FOR_TEST =
    CREATE_OFFICIAL_CLARIFICATION_MUTATION;

type UpdateClarificationMutationData = {
    updateOfficialClarification: {
        updatedClarification: OfficialClarification | null | undefined;
        error?:
            | {
                  code: string;
              }
            | null
            | undefined;
    };
};

type UpdateClarificationVariables = {
    id: string;
    clarification: {
        youtubeId: string;
        text: string;
        openTimestamp: number;
        closeTimestamp: number;
    };
};

const UPDATE_OFFICIAL_CLARIFICATION_MUTATION: GqlOperation<
    UpdateClarificationMutationData,
    UpdateClarificationVariables
> = gqlOp(gql`
    mutation updateOfficialClarification(
        $id: ID!
        $clarification: UpdateOfficialClarificationInput!
    ) {
        updateOfficialClarification(id: $id, clarification: $clarification) {
            updatedClarification {
                ...clarificationFragmentWithReporter
            }
            error {
                code
            }
        }
    }

    ${officialClarificationWithReporterFragment}
`);
export const UPDATE_OFFICIAL_CLARIFICATION_MUTATION_FOR_TEST =
    UPDATE_OFFICIAL_CLARIFICATION_MUTATION;

type DeleteClarificationMutationData = {
    deleteOfficialClarification: {
        error?:
            | {
                  code: string;
              }
            | null
            | undefined;
    };
};

type DeleteClarificationVariables = {
    id: string;
};

const DELETE_OFFICIAL_CLARIFICATION_MUTATION: GqlOperation<
    DeleteClarificationMutationData,
    DeleteClarificationVariables
> = gqlOp(gql`
    mutation deleteOfficialClarification($id: ID!) {
        deleteOfficialClarification(id: $id) {
            error {
                code
            }
        }
    }
`);
export const DELETE_OFFICIAL_CLARIFICATION_MUTATION_FOR_TEST =
    DELETE_OFFICIAL_CLARIFICATION_MUTATION;

export function getOfficialClarifications(
    gqlClient: ApolloGqlClient,
    youtubeId: string,
): Promise<Array<OfficialClarification>> {
    return gqlClient
        .query(GET_OFFICIAL_CLARIFICATIONS_WITH_REPORTER_QUERY, {
            variables: {
                youtubeId,
            },
            fetchPolicy: "network-only",
        })
        .then((result) => {
            // TODO(somewhatabstract): Data can be null.
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            return result.data.officialClarifications;
        })
        .catch((e) => {
            throw new KAError(
                "Failed to fetch clarifications",
                Errors.Internal,
                {cause: e},
            );
        });
}

export function createOfficialClarification(
    gqlClient: ApolloGqlClient,
    youtubeId: string,
    reporterKaid: string,
    text: string,
    openTimestamp: number,
    closeTimestamp: number,
): Promise<OfficialClarification> {
    // @ts-expect-error - TS2322 - Type 'Promise<OfficialClarification | null | undefined>' is not assignable to type 'Promise<OfficialClarification>'.
    return gqlClient
        .mutate(CREATE_OFFICIAL_CLARIFICATION_MUTATION, {
            variables: {
                clarification: {
                    youtubeId,
                    reporterKaid,
                    openTimestamp,
                    closeTimestamp,
                    text,
                },
            },
            refetchQueries: ["getOfficialClarificationsWithReporter"],
        })
        .then((result) => {
            if (result.data?.createOfficialClarification.error) {
                throw new KAError(
                    result.data.createOfficialClarification.error.code,
                    Errors.GraphqlResponse,
                );
            }

            // TODO(somewhatabstract): Data could be null.
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            return result.data.createOfficialClarification.newClarification;
        })
        .catch((e) => {
            throw new KAError(
                "Failed to create new clarification",
                Errors.Internal,
                {cause: e},
            );
        });
}

export function updateOfficialClarification(
    gqlClient: ApolloGqlClient,
    clarificationId: string,
    youtubeId: string,
    text: string,
    openTimestamp: number,
    closeTimestamp: number,
): Promise<OfficialClarification> {
    // @ts-expect-error - TS2322 - Type 'Promise<OfficialClarification | null | undefined>' is not assignable to type 'Promise<OfficialClarification>'.
    return gqlClient
        .mutate(UPDATE_OFFICIAL_CLARIFICATION_MUTATION, {
            variables: {
                id: clarificationId,
                clarification: {
                    youtubeId,
                    openTimestamp,
                    closeTimestamp,
                    text,
                },
            },
            refetchQueries: ["getOfficialClarificationsWithReporter"],
        })
        .then((result) => {
            if (result.data?.updateOfficialClarification.error) {
                throw new KAError(
                    result.data.updateOfficialClarification.error.code,
                    Errors.GraphqlResponse,
                );
            }

            // TODO(somewhatabstract): Data can be null.
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            return result.data.updateOfficialClarification.updatedClarification;
        })
        .catch((e) => {
            throw new KAError(
                "Failed to update clarification",
                Errors.Internal,
                {cause: e},
            );
        });
}

export function deleteOfficialClarification(
    gqlClient: ApolloGqlClient,
    clarificationId: string,
): Promise<boolean> {
    return gqlClient
        .mutate(DELETE_OFFICIAL_CLARIFICATION_MUTATION, {
            variables: {
                id: clarificationId,
            },
            refetchQueries: ["getOfficialClarificationsWithReporter"],
        })
        .then((result) => {
            if (result.data?.deleteOfficialClarification.error) {
                throw new KAError(
                    result.data.deleteOfficialClarification.error.code,
                    Errors.GraphqlResponse,
                );
            }

            return true;
        })
        .catch((e) => {
            throw new KAError(
                "Failed to delete clarification",
                Errors.Internal,
                {cause: e},
            );
        });
}
